import React from 'react';
import { Typography, Paper, styled } from '@mui/material';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import type { Components } from 'react-markdown';
import SafeHTMLRenderer from './SafeHTMLRenderer';
import DocumentRenderer from './DocumentRenderer';
import { Message } from '../../stores/chatStore';
import { isHtmlContent } from '../../utils/htmlSanitizer';
import { isJsonContent, extractJsonFromContent, formatTaskResponse, formatJsonAsHtml } from '../../utils/jsonFormatter';

interface MessageRendererProps {
  message: Message;
  showRaw?: boolean;
  onInteraction?: (action: string, data: any) => void;
  onAttachDocument?: (documentId: string) => void;
  onDetachDocument?: (documentId: string) => void;
  onViewDocument?: (url: string) => void;
  onSearchDocuments?: () => void;
}

const CodeBlock = styled(Paper)(({ theme }) => ({
  backgroundColor: theme.palette.mode === 'dark' ? theme.palette.grey[900] : theme.palette.grey[100],
  padding: theme.spacing(2),
  overflow: 'auto',
  fontFamily: 'Consolas, Monaco, "Andale Mono", "Ubuntu Mono", monospace',
  fontSize: '0.9rem',
  borderRadius: theme.shape.borderRadius,
  margin: theme.spacing(1, 0),
  '& code': {
    fontFamily: 'inherit',
  }
}));

const InlineCode = styled('code')(({ theme }) => ({
  backgroundColor: theme.palette.mode === 'dark' ? theme.palette.grey[800] : theme.palette.grey[200],
  padding: '2px 4px',
  borderRadius: '3px',
  fontFamily: 'Consolas, Monaco, "Andale Mono", "Ubuntu Mono", monospace',
  fontSize: '0.9em',
}));

const MarkdownRenderer: React.FC<{ content: string }> = ({ content }) => {
  const components: Partial<Components> = {
    // Custom code block rendering
    code({ node, className, children, ...props }) {
      const match = /language-(\w+)/.exec(className || '');
      const isInline = !match && !props.style;
      
      if (isInline) {
        return <InlineCode>{children}</InlineCode>;
      }
      
      return (
        <CodeBlock elevation={0}>
          <code className={className} {...props}>
            {children}
          </code>
        </CodeBlock>
      );
    },
    // Custom paragraph rendering to handle spacing
    p({ children }) {
      return (
        <Typography variant="body1" component="p" style={{ marginBottom: 12 }}>
          {children}
        </Typography>
      );
    },
    // Custom link rendering for security
    a({ href, children }) {
      return (
        <a href={href} target="_blank" rel="noopener noreferrer">
          {children}
        </a>
      );
    },
    // Custom list rendering
    ul({ children }) {
      return (
        <ul style={{ paddingLeft: 24, marginBottom: 12 }}>
          {children}
        </ul>
      );
    },
    ol({ children }) {
      return (
        <ol style={{ paddingLeft: 24, marginBottom: 12 }}>
          {children}
        </ol>
      );
    },
  };

  return (
    <ReactMarkdown
      remarkPlugins={[remarkGfm]}
      components={components}
    >
      {content}
    </ReactMarkdown>
  );
};

const TextRenderer: React.FC<{ content: string }> = ({ content }) => {
  return (
    <Typography
      variant="body1"
      component="div"
      style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}
    >
      {content}
    </Typography>
  );
};

const JsonRenderer: React.FC<{ content: string }> = ({ content }) => {
  const jsonData = extractJsonFromContent(content);

  if (!jsonData) {
    return <TextRenderer content={content} />;
  }

  // Special handling for task creation responses
  if (jsonData.json.task && jsonData.json.message) {
    const formattedHtml = formatTaskResponse(jsonData.json);
    return (
      <div>
        {jsonData.remainingText && (
          <TextRenderer content={jsonData.remainingText} />
        )}
        <SafeHTMLRenderer
          content={formattedHtml}
          hints={{ sanitizationLevel: 'moderate' }}
        />
      </div>
    );
  }

  // General JSON formatting
  const formattedHtml = formatJsonAsHtml(jsonData.json, 'Data');
  return (
    <div>
      {jsonData.remainingText && (
        <TextRenderer content={jsonData.remainingText} />
      )}
      <SafeHTMLRenderer
        content={formattedHtml}
        hints={{ sanitizationLevel: 'moderate' }}
      />
    </div>
  );
};

const MessageRenderer: React.FC<MessageRendererProps> = ({
  message,
  showRaw = false,
  onInteraction,
  onAttachDocument,
  onDetachDocument,
  onViewDocument,
  onSearchDocuments
}) => {
  const { content, content_type, rendering_hints, metadata } = message;

  // If showing raw content or if it's a user message, always show as text
  if (showRaw || message.role === 'user') {
    return <TextRenderer content={content} />;
  }

  // Check if this message should use document-specific rendering
  const documentRenderer = (
    <DocumentRenderer
      content={content}
      messageMetadata={metadata}
      onAttachDocument={onAttachDocument}
      onDetachDocument={onDetachDocument}
      onViewDocument={onViewDocument}
      onSearchDocuments={onSearchDocuments}
    />
  );

  // If DocumentRenderer returns content, wrap it with the regular content
  if (documentRenderer) {
    // Determine content type for the main content
    const actualContentType = (() => {
      const isJson = isJsonContent(content);
      if (isJson) return 'json';

      // If content_type is already set and it's not JSON, use it
      if (content_type) return content_type;

      // Otherwise detect content type
      if (isHtmlContent(content)) return 'html';
      return message.role === 'assistant' ? 'markdown' : 'text';
    })();

    let mainContent;
    switch (actualContentType) {
      case 'html':
        mainContent = (
          <SafeHTMLRenderer
            content={content}
            hints={rendering_hints || { sanitizationLevel: 'moderate' }}
          />
        );
        break;
      case 'json':
        mainContent = <JsonRenderer content={content} />;
        break;
      case 'markdown':
        mainContent = <MarkdownRenderer content={content} />;
        break;
      default:
        mainContent = <TextRenderer content={content} />;
        break;
    }

    return (
      <div>
        {mainContent}
        {documentRenderer}
      </div>
    );
  }

  // Fallback to normal rendering if no document content
  // Determine content type
  // ALWAYS check for JSON first, even if content_type is already set
  const actualContentType = (() => {
    const isJson = isJsonContent(content);
    if (isJson) return 'json';

    // If content_type is already set and it's not JSON, use it
    if (content_type) return content_type;

    // Otherwise detect content type
    if (isHtmlContent(content)) return 'html';
    return message.role === 'assistant' ? 'markdown' : 'text';
  })();


  switch (actualContentType) {
    case 'html':
      return (
        <SafeHTMLRenderer
          content={content}
          hints={rendering_hints || { sanitizationLevel: 'moderate' }}
        />
      );

    case 'json':
      return <JsonRenderer content={content} />;

    case 'markdown':
      return <MarkdownRenderer content={content} />;

    default:
      return <TextRenderer content={content} />;
  }
};

export default MessageRenderer;